/**
 * Blog Table of Contents (TOC) Script
 * Generates dynamic TOC with sticky behavior and scroll spy
 */

class BlogTableOfContents {
  constructor() {
    this.tocContainer = document.getElementById('blog-toc-sidebar');
    this.tocList = document.getElementById('toc-list');
    this.tocListMobile = document.getElementById('toc-list-mobile');
    this.tocMobileToggle = document.getElementById('toc-mobile-toggle');
    this.tocMobileButton = this.tocMobileToggle?.querySelector('.toc-mobile-button');
    this.tocMobileContent = document.getElementById('toc-mobile-content');
    
    // Mobile TOC at top of article
    this.tocMobileWrapper = document.getElementById('toc-mobile-wrapper');
    this.tocMobileToggleArticle = document.getElementById('toc-mobile-toggle-article');
    this.tocMobileButtonArticle = this.tocMobileToggleArticle?.querySelector('.toc-mobile-button');
    this.tocMobileContentArticle = document.getElementById('toc-mobile-content-article');
    this.tocListMobileArticle = document.getElementById('toc-list-mobile-article');
    
    this.articleContent = document.querySelector('.article-content');
    this.headings = [];
    this.tocItems = [];
    this.isSticky = false;
    this.headerHeight = this.getHeaderHeight();
    this.sidebarWidth = this.getSidebarWidth();
    
    this.init();
  }

  init() {
    console.log('TOC: Starting initialization...');
    console.log('TOC: Article content found:', !!this.articleContent);
    console.log('TOC: TOC container found:', !!this.tocContainer);
    console.log('TOC: Mobile wrapper found:', !!this.tocMobileWrapper);
    
    if (!this.articleContent) {
      console.log('TOC: No article content found, exiting');
      return;
    }
    
    // Check if TOC is enabled (elements exist)
    if (!this.tocContainer && !this.tocMobileWrapper) {
      console.log('TOC: Disabled - no TOC elements found');
      return;
    }
    
    this.findHeadings();
    console.log('TOC: Found', this.headings.length, 'headings');
    
    if (this.headings.length < 3) {
      console.log('TOC: Not enough headings (need 3+), hiding TOC');
      this.hideTOC();
      return;
    }
    
    console.log('TOC: Generating TOC with', this.headings.length, 'headings');
    this.generateTOC();
    this.setupEventListeners();
    this.updateActiveSection();
    
    // Set CSS custom properties
    document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
    document.documentElement.style.setProperty('--sidebar-width', `${this.sidebarWidth}px`);
    
    console.log('TOC: Initialization complete');
  }

  findHeadings() {
    const headingSelectors = 'h2, h3';
    const headingElements = this.articleContent.querySelectorAll(headingSelectors);
    
    headingElements.forEach((heading, index) => {
      // Create unique ID if it doesn't exist
      if (!heading.id) {
        const text = heading.textContent.trim();
        const id = this.createSlug(text) + '-' + index;
        heading.id = id;
      }
      
      this.headings.push({
        element: heading,
        id: heading.id,
        text: heading.textContent.trim(),
        level: parseInt(heading.tagName.charAt(1))
      });
    });
  }

  createSlug(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  generateTOC() {
    if (this.headings.length === 0) return;
    
    const desktopHTML = this.generateTOCHTML();
    const mobileHTML = this.generateTOCHTML(true);
    
    // Sidebar TOC
    if (this.tocList) {
      this.tocList.innerHTML = desktopHTML;
    }
    
    if (this.tocListMobile) {
      this.tocListMobile.innerHTML = mobileHTML;
    }
    
    // Article top mobile TOC
    if (this.tocListMobileArticle) {
      this.tocListMobileArticle.innerHTML = mobileHTML;
    }
    
    // Bind events after generating TOC
    this.bindTOCEvents();
  }

  generateTOCHTML(isMobile = false) {
    return this.headings.map(heading => {
      const levelClass = `toc-h${heading.level}`;
      return `
        <li class="${levelClass}">
          <a href="#${heading.id}" data-target="${heading.id}">
            ${heading.text}
          </a>
        </li>
      `;
    }).join('');
  }

  setupEventListeners() {
    // Scroll spy
    window.addEventListener('scroll', this.throttle(this.handleScroll.bind(this), 100));
    
    // Resize handler
    window.addEventListener('resize', this.throttle(this.handleResize.bind(this), 250));
    
    // Mobile toggle in sidebar
    if (this.tocMobileButton) {
      this.tocMobileButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMobileTOC();
      });
    }
    
    // Mobile toggle at top of article
    if (this.tocMobileButtonArticle) {
      this.tocMobileButtonArticle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMobileTOCArticle();
      });
    }
  }

  bindTOCEvents() {
    // Bind click events for dynamically generated TOC links
    const allTOCLinks = document.querySelectorAll('.toc-list a, .toc-list-mobile a');
    allTOCLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        this.handleTOCClick(e);
      });
    });
    
    // Update tocItems for scroll spy
    this.tocItems = allTOCLinks;
  }

  handleTOCClick(e) {
    e.preventDefault();
    const targetId = e.target.getAttribute('data-target');
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      const offsetTop = targetElement.getBoundingClientRect().top + window.pageYOffset - this.headerHeight - 20;
      
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      
      // Close mobile TOC after click
      if (window.innerWidth <= 1024) {
        this.closeMobileTOC();
        this.closeMobileTOCArticle();
      }
    }
  }

  handleScroll() {
    this.updateStickyBehavior();
    this.updateActiveSection();
  }

  updateStickyBehavior() {
    if (window.innerWidth <= 1024) {
      this.removeSticky();
      return;
    }
    
    const tocOriginalPosition = this.getTOCOriginalPosition();
    const articleBottom = this.getArticleBottom();
    const scrollTop = window.pageYOffset;
    
    // Check if we should activate sticky
    const shouldBeSticky = scrollTop > tocOriginalPosition && scrollTop < articleBottom - window.innerHeight + 200;
    
    if (shouldBeSticky && !this.isSticky) {
      this.activateSticky();
    } else if (!shouldBeSticky && this.isSticky) {
      this.removeSticky();
    }
  }

  getTOCOriginalPosition() {
    if (!this.tocContainer) return 0;
    
    // Get the position where TOC should become sticky (after recent posts)
    const recentPostsBlock = document.querySelector('.sidebar-recent-post');
    if (recentPostsBlock) {
      const rect = recentPostsBlock.getBoundingClientRect();
      return rect.bottom + window.pageYOffset;
    }
    
    const rect = this.tocContainer.getBoundingClientRect();
    return rect.top + window.pageYOffset;
  }

  getArticleBottom() {
    if (!this.articleContent) return 0;
    const rect = this.articleContent.getBoundingClientRect();
    return rect.bottom + window.pageYOffset;
  }

  activateSticky() {
    if (!this.tocContainer) return;
    
    this.isSticky = true;
    this.tocContainer.classList.add('toc-sticky');
    
    // Set fixed width when sticky
    this.tocContainer.style.width = '256px';
    
    // Adjust position based on sidebar
    const sidebar = document.querySelector('.page-sidebar');
    if (sidebar) {
      const sidebarRect = sidebar.getBoundingClientRect();
      this.tocContainer.style.left = `${sidebarRect.left}px`;
    }
  }

  removeSticky() {
    if (!this.tocContainer) return;
    
    this.isSticky = false;
    this.tocContainer.classList.remove('toc-sticky');
    this.tocContainer.style.left = '';
    this.tocContainer.style.width = '';
  }

  updateActiveSection() {
    if (this.headings.length === 0) return;
    
    const scrollTop = window.pageYOffset + this.headerHeight + 50;
    let activeHeading = null;
    
    // Find the current active heading
    for (let i = this.headings.length - 1; i >= 0; i--) {
      const heading = this.headings[i];
      const headingTop = heading.element.getBoundingClientRect().top + window.pageYOffset;
      
      if (scrollTop >= headingTop) {
        activeHeading = heading;
        break;
      }
    }
    
    // Update active states
    this.tocItems.forEach(item => {
      item.classList.remove('active');
      if (activeHeading && item.getAttribute('data-target') === activeHeading.id) {
        item.classList.add('active');
      }
    });
  }

  toggleMobileTOC() {
    if (!this.tocMobileContent || !this.tocMobileButton) return;
    
    const isExpanded = this.tocMobileButton.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.closeMobileTOC();
    } else {
      this.openMobileTOC();
    }
  }

  openMobileTOC() {
    if (!this.tocMobileContent || !this.tocMobileButton) return;
    
    this.tocMobileButton.setAttribute('aria-expanded', 'true');
    this.tocMobileContent.classList.add('expanded');
  }

  closeMobileTOC() {
    if (!this.tocMobileContent || !this.tocMobileButton) return;
    
    this.tocMobileButton.setAttribute('aria-expanded', 'false');
    this.tocMobileContent.classList.remove('expanded');
  }

  toggleMobileTOCArticle() {
    if (!this.tocMobileContentArticle || !this.tocMobileButtonArticle) {
      console.error('Mobile TOC elements not found:', {
        button: !!this.tocMobileButtonArticle,
        content: !!this.tocMobileContentArticle
      });
      return;
    }
    
    const isExpanded = this.tocMobileButtonArticle.getAttribute('aria-expanded') === 'true';
    console.log('Toggling mobile TOC, currently expanded:', isExpanded);
    
    if (isExpanded) {
      this.closeMobileTOCArticle();
    } else {
      this.openMobileTOCArticle();
    }
  }

  openMobileTOCArticle() {
    if (!this.tocMobileContentArticle || !this.tocMobileButtonArticle) return;
    
    console.log('Opening mobile TOC');
    this.tocMobileButtonArticle.setAttribute('aria-expanded', 'true');
    this.tocMobileContentArticle.classList.add('expanded');
    
    // Force height calculation
    const scrollHeight = this.tocMobileContentArticle.scrollHeight;
    this.tocMobileContentArticle.style.maxHeight = Math.max(scrollHeight, 300) + 'px';
    
    console.log('Mobile TOC opened, height set to:', this.tocMobileContentArticle.style.maxHeight);
  }

  closeMobileTOCArticle() {
    if (!this.tocMobileContentArticle || !this.tocMobileButtonArticle) return;
    
    console.log('Closing mobile TOC');
    this.tocMobileButtonArticle.setAttribute('aria-expanded', 'false');
    this.tocMobileContentArticle.classList.remove('expanded');
    this.tocMobileContentArticle.style.maxHeight = '0px';
  }

  handleResize() {
    this.headerHeight = this.getHeaderHeight();
    this.sidebarWidth = this.getSidebarWidth();
    
    document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
    document.documentElement.style.setProperty('--sidebar-width', `${this.sidebarWidth}px`);
    
    if (window.innerWidth <= 1024) {
      this.removeSticky();
    }
  }

  getHeaderHeight() {
    const header = document.querySelector('header, .header, .site-header, #header');
    return header ? header.offsetHeight : 80;
  }

  getSidebarWidth() {
    const sidebar = document.querySelector('.page-sidebar');
    return sidebar ? sidebar.offsetWidth : 300;
  }

  hideTOC() {
    if (this.tocContainer) {
      this.tocContainer.style.display = 'none';
    }
    if (this.tocMobileWrapper) {
      this.tocMobileWrapper.style.display = 'none';
    }
  }

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing TOC...');
  
  // Initialize on any page for demo purposes, or on article pages
  if (document.body.classList.contains('template-article') || 
      document.querySelector('.article-template') ||
      document.querySelector('.article-content') ||
      window.location.pathname.includes('/blogs/') ||
      window.location.pathname.includes('toc-demo')) {
    
    console.log('Article page detected, creating TOC instance');
    new BlogTableOfContents();
  } else {
    console.log('Not an article page, skipping TOC initialization');
  }
});

// Fallback function for mobile TOC toggle (can be called directly)
window.toggleMobileTOC = function() {
  console.log('Global toggleMobileTOC called');
  const button = document.querySelector('#toc-mobile-toggle-article .toc-mobile-button');
  const content = document.getElementById('toc-mobile-content-article');
  
  console.log('Button found:', !!button);
  console.log('Content found:', !!content);
  
  if (!button || !content) {
    console.error('Mobile TOC elements not found');
    return;
  }
  
  const isExpanded = button.getAttribute('aria-expanded') === 'true';
  console.log('Currently expanded:', isExpanded);
  
  if (isExpanded) {
    button.setAttribute('aria-expanded', 'false');
    content.classList.remove('expanded');
    content.style.maxHeight = '0px';
    console.log('TOC closed');
  } else {
    button.setAttribute('aria-expanded', 'true');
    content.classList.add('expanded');
    content.style.maxHeight = '600px';
    console.log('TOC opened');
  }
};

// Export for potential external use
window.BlogTableOfContents = BlogTableOfContents;